import { createContext, useContext, ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Itinerary, ItinerarySummary } from '../types/itinerary';
import { getItineraryDocId, getLegacyItineraryDocId } from '../utils/itineraryComparator';

interface ItineraryContextType {
  getDepartureDatesForShip: (cruiseLine: string, shipName: string) => Promise<string[]>;
  getItinerarySummaryForShip: (cruiseLine: string, shipName: string) => Promise<ItinerarySummary | null>;
  getItineraryByDate: (cruiseLine: string, shipName: string, sailingDate: string) => Promise<Itinerary | null>;
  getItineraryByCompositeKey: (cruiseLine: string, shipName: string, compositeKey: string) => Promise<Itinerary | null>;
}

const ItineraryContext = createContext<ItineraryContextType | null>(null);

export const useItinerary = () => {
  const context = useContext(ItineraryContext);
  if (!context) {
    throw new Error('useItinerary must be used within an ItineraryProvider');
  }
  return context;
};

interface ItineraryProviderProps {
  children: ReactNode;
}

export const ItineraryProvider = ({ children }: ItineraryProviderProps) => {
  /**
   * Stage 1: Get departure dates for a ship (1 Firestore read)
   * Fetches from itinerarySummaries collection
   */
  const getDepartureDatesForShip = async (
    cruiseLine: string,
    shipName: string
  ): Promise<string[]> => {
    // Check if online
    if (!navigator.onLine) {
      return [];
    }

    try {
      const docId = `${cruiseLine}_${shipName}`;
      const summaryRef = doc(db, 'itinerarySummaries', docId);
      const summaryDoc = await getDoc(summaryRef);

      if (summaryDoc.exists()) {
        const data = summaryDoc.data() as ItinerarySummary;
        return data.departureDates || [];
      }

      return [];
    } catch (error: any) {
      // Suppress permission errors that occur after logout - these are expected
      // and will be handled by the calling component checking auth state
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        // Silently return empty array - the calling component should check auth state
        return [];
      }
      console.error('Error fetching departure dates:', error);
      return [];
    }
  };

  /**
   * Get full itinerary summary for a ship (1 Firestore read)
   * Fetches from itinerarySummaries collection, includes itineraryDetails
   */
  const getItinerarySummaryForShip = async (
    cruiseLine: string,
    shipName: string
  ): Promise<ItinerarySummary | null> => {
    // Check if online
    if (!navigator.onLine) {
      return null;
    }

    try {
      const docId = `${cruiseLine}_${shipName}`;
      const summaryRef = doc(db, 'itinerarySummaries', docId);
      const summaryDoc = await getDoc(summaryRef);

      if (summaryDoc.exists()) {
        const data = summaryDoc.data() as ItinerarySummary;
        return data;
      }

      return null;
    } catch (error: any) {
      // Suppress permission errors that occur after logout - these are expected
      // and will be handled by the calling component checking auth state
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        // Silently return null - the calling component should check auth state
        return null;
      }
      console.error('Error fetching itinerary summary:', error);
      return null;
    }
  };

  /**
   * Stage 2: Get full itinerary for a specific date (1 Firestore read)
   * Fetches from itineraries collection. Legacy: uses old docId format.
   */
  const getItineraryByDate = async (
    cruiseLine: string,
    shipName: string,
    sailingDate: string
  ): Promise<Itinerary | null> => {
    return getItineraryByCompositeKey(cruiseLine, shipName, sailingDate);
  };

  /**
   * Get full itinerary by composite key (sailingDate_returnDate) or legacy plain date.
   * Fallback: for plain-date keys, tries old docId first for backward compatibility.
   */
  const getItineraryByCompositeKey = async (
    cruiseLine: string,
    shipName: string,
    compositeKey: string
  ): Promise<Itinerary | null> => {
    if (!navigator.onLine) {
      return null;
    }

    try {
      const isLegacyFormat = !compositeKey.includes('_') || compositeKey.split('_').length < 2;

      if (isLegacyFormat) {
        // Plain date: try old docId first (backward compat for past itineraries)
        const oldDocId = getLegacyItineraryDocId(cruiseLine, shipName, compositeKey);
        const oldDocRef = doc(db, 'itineraries', oldDocId);
        const oldDoc = await getDoc(oldDocRef);
        if (oldDoc.exists()) {
          return oldDoc.data() as Itinerary;
        }
        return null;
      }

      // Composite key: use new docId format
      const [sailingDate, returnDate] = compositeKey.split('_', 2);
      const newDocId = getItineraryDocId(cruiseLine, shipName, sailingDate, returnDate);
      const newDocRef = doc(db, 'itineraries', newDocId);
      const newDoc = await getDoc(newDocRef);
      if (newDoc.exists()) {
        return newDoc.data() as Itinerary;
      }
      return null;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        return null;
      }
      console.error('Error fetching itinerary:', error);
      return null;
    }
  };

  const value = {
    getDepartureDatesForShip,
    getItinerarySummaryForShip,
    getItineraryByDate,
    getItineraryByCompositeKey
  };

  return (
    <ItineraryContext.Provider value={value}>
      {children}
    </ItineraryContext.Provider>
  );
};

